// Phase APP-2 · KPI strip for the admin approvals page.
// 6 cards (stacks on <640px). Polls /api/approvals/metrics on mount and
// every 30s. Uses useTimedFetch so we never spin forever — 15s fallback
// shows an inline retry card.
import { useEffect } from "react";
import { Loader2, RefreshCw, Clock, Inbox, Check, X, Repeat, Shield } from "lucide-react";
import useTimedFetch from "../../../hooks/useTimedFetch";

const ORANGE = "#FF6B00";

function Card({ label, value, Icon, accent, testid }) {
  const accentCls = accent === "yellow"
        ? "border-yellow-300 bg-yellow-50"
      : accent === "purple"
        ? "border-purple-300 bg-purple-50"
      : accent === "orange"
        ? "border-[#FF6B00] bg-orange-50"
      : "border-slate-200 bg-white";
  const accentText = accent === "yellow" ? "text-yellow-700"
                    : accent === "purple" ? "text-purple-700"
                    : accent === "orange" ? "text-[#C2410C]"
                    : "text-slate-900";
  return (
    <div data-testid={testid}
         className={`flex-1 min-w-[140px] border rounded-2xl p-4 transition-colors ${accentCls}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className={"h-4 w-4 " + (accent ? accentText : "text-slate-400")}/>
      </div>
      <div className={"text-3xl font-bold mt-1 font-['Outfit'] " + accentText}>{value ?? 0}</div>
    </div>
  );
}

export default function AdminApprovalsKPIs() {
  const { data, loading, error, reload } = useTimedFetch("/approvals/metrics");
  // 30s poll
  useEffect(() => {
    const id = setInterval(reload, 30_000);
    return () => clearInterval(id);
  }, [reload]);

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-2xl p-3 flex items-center gap-3"
           data-testid="approvals-kpis-error">
        <span className="text-rose-700 text-sm flex-1">Couldn't load KPIs · {error.message}</span>
        <button onClick={reload} data-testid="approvals-kpis-retry"
                className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-white"
                style={{ backgroundColor: ORANGE }}>
          <RefreshCw className="h-3 w-3"/> Retry
        </button>
      </div>
    );
  }
  if (loading && !data) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="approvals-kpis-loading">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-24 bg-slate-100 rounded-2xl animate-pulse"/>
        ))}
      </div>
    );
  }
  const k = data || {};
  const overAvg = (k.avg_approval_time_hours || 0) > 24;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="approvals-kpis">
      <Card testid="kpi-pending"          label="Pending"             value={k.pending_count}          Icon={Inbox} accent={(k.pending_count || 0) > 0 ? "yellow" : ""}/>
      <Card testid="kpi-approved-today"   label="Approved today"      value={k.approved_today}         Icon={Check}/>
      <Card testid="kpi-rejected-today"   label="Rejected today"      value={k.rejected_today}         Icon={X}/>
      <Card testid="kpi-resubs-pending"   label="Resubmissions"       value={k.resubmissions_pending}  Icon={Repeat}/>
      <Card testid="kpi-overrides"        label="Founder overrides"   value={k.founder_overrides_count} Icon={Shield} accent="purple"/>
      <Card testid="kpi-avg-approval"     label="Avg approval (h)"    value={k.avg_approval_time_hours} Icon={Clock} accent={overAvg ? "orange" : ""}/>
      {loading && <span className="hidden"><Loader2/></span>}
    </div>
  );
}
